// @ts-nocheck
import AlgoliaSortingSelector from '@components/algolia/AlgoliaSortingSelector'
import CustomRefinementList from '@components/algolia/CustomRefinementList'
import { ChevronDown, ChevronUp } from '@components/icons'
import filtersToHide from '@config/filters_hidden_in_collections.json'
import { getFilterAttributeTitle } from '@lib/algolia'
import { slugify } from '@lib/helpers/general'
import cn from 'clsx'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  connectCurrentRefinements,
  connectDynamicWidgets,
} from 'react-instantsearch-dom'
interface props {
  attributesToRender: string[]
  collectionHandle: string
  indexName: string
  defaultSortIndex?: string
  specialPrice?: boolean
  onSpecialPriceChange?: (value: boolean) => void
  promoOff?: boolean
  onPromoOffChange?: (value: boolean) => void
}

const RefinementSummary = connectCurrentRefinements(({ items, attribute }) => {
  const group = items.find((item: any) => item.attribute === attribute)
  const labels = (group?.items ?? []).map((refinement: any) =>
    String(refinement.label ?? '').replace(/_/g, ' ')
  )
  if (!labels.length) return null
  const shown = labels.slice(0, 3).join(', ')
  const extra = labels.length - 3
  return (
    <span className="mt-1 block truncate pr-6 body-1 text-text-disabled">
      <span className="capitalize">{shown}</span>
      {extra > 0 && ` +${extra} more`}
    </span>
  )
})

interface SpecialPriceToggleProps {
  checked: boolean
  onChange: (value: boolean) => void
  label?: string
}

const SpecialPriceToggle: FC<SpecialPriceToggleProps> = ({
  checked,
  onChange,
  label = 'Special price',
}) => (
  <div className="flex items-center justify-between py-5 border-b border-ui-grey-25">
    <span className="body-1-bold text-text-base">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer',
        {
          'bg-brand-chrome': checked,
          'bg-ui-grey-25': !checked,
        }
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out',
          {
            'translate-x-5': checked,
            'translate-x-0.5': !checked,
          }
        )}
      />
    </button>
  </div>
)

interface FilterAccordionProps {
  title: string
  hidden: boolean
  summary?: React.ReactNode
  children: React.ReactNode
}

const FilterAccordion: FC<FilterAccordionProps> = ({
  title,
  hidden,
  summary,
  children,
}) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={cn('border-b border-ui-grey-25', { hidden })}>
      <button
        type="button"
        aria-expanded={open}
        className="w-full flex items-start justify-between py-5 text-left"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flex flex-col">
          <span className="body-1-bold text-text-base capitalize">{title}</span>
          {!open && summary}
        </span>
        <span className="pt-1 shrink-0">
          {open ? <ChevronUp /> : <ChevronDown />}
        </span>
      </button>
      <div className={cn('pb-5', { hidden: !open })}>{children}</div>
    </div>
  )
}

const DynamicAlgoliaFilters: FC<props> = ({
  indexName,
  collectionHandle,
  attributesToRender,
  defaultSortIndex,
  specialPrice = false,
  onSpecialPriceChange,
  promoOff = false,
  onPromoOffChange,
}) => {
  const [filteredAttributesToRender, setFilteredAttributesToRender] = useState<
    string[]
  >([])
  const [noOptions, setNoOptions] = useState<{ [key: string]: boolean }>({})

  const defaultIndex = defaultSortIndex ?? indexName

  useEffect(() => {
    setFilteredAttributesToRender(
      attributesToRender.filter((attribute) => {
        if (
          collectionHandle &&
          filtersToHide[attribute]?.includes(collectionHandle)
        ) {
          return false
        }
        return true
      })
    )
  }, [collectionHandle]) // Deliberately not add attributesToRender as dependency. It caused filters selection to reset.

  const hasOptions = useCallback((attr, has) => {
    setNoOptions((prev) =>
      prev[attr] === !has ? prev : { ...prev, [attr]: !has }
    )
  }, [])

  return (
    <div className="flex flex-col">
      <AlgoliaSortingSelector
        indexName={indexName}
        defaultIndex={defaultIndex}
      />

      {onSpecialPriceChange && (
        <SpecialPriceToggle
          checked={specialPrice}
          onChange={onSpecialPriceChange}
          label="Clearance"
        />
      )}

      {onPromoOffChange && (
        <SpecialPriceToggle
          checked={promoOff}
          onChange={onPromoOffChange}
          label="Special Offers"
        />
      )}

      {filteredAttributesToRender.map((attribute: string, index: number) => {
        const title = getFilterAttributeTitle(attribute)
        const key = slugify(`filter` + attribute + index)
        return (
          <FilterAccordion
            key={key}
            title={title}
            hidden={noOptions[attribute]}
            summary={<RefinementSummary attribute={attribute} />}
          >
            <div className="capitalize">
              <CustomRefinementList
                hasOptions={hasOptions}
                attribute={attribute}
                limit={50}
              />
            </div>
          </FilterAccordion>
        )
      })}

      <style jsx global>
        {`
          .ais-ClearRefinements-button {
            text-decoration: underline;
          }
          .ais-RefinementList-item--selected {
            border: 1px solid #464544 !important;
            border-radius: 4px;
          }
          .ais-RefinementList-item {
            border: 1px solid transparent;
            border-radius: 4px;
          }
        `}
      </style>
    </div>
  )
}

const AlgoliaFilters = connectDynamicWidgets(DynamicAlgoliaFilters)

export default AlgoliaFilters
